import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { FormField, InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { TabGroup } from '@/kit/Tabs';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { practicePackService } from '@/core/services/practicePack';
import {
  PACK_ITEM_TYPES,
  PackConflictResolution,
  PackLayer,
  type GoldenFixtureResult,
  type GoldenFixtureRow,
  type GoldenRunResult,
  type PackConflictRow,
  type PackLayerRow,
  type PackPreviewRow,
  type PackUpgradeDetail,
  type PracticePackRow,
} from '@/core/models/phase7.models';
import { useMounted } from './useMounted';
import './LeafTables.scss';
import './RegulatoryPackUpgrade.scss';

const ALL = '';

interface ResolutionOption {
  id: PackConflictResolution;
  name: string;
}

const resolutionOptions: ResolutionOption[] = [
  { id: PackConflictResolution.KeepPractice, name: 'Keep the practice customisation' },
  { id: PackConflictResolution.TakeBase, name: 'Take the regulatory pack' },
];

const packFrameworks = ['FRS102-1A', 'FRS102', 'FRS105', 'NONSTAT'];

const itemTypeOptions = [{ id: ALL, name: 'All types' }, ...PACK_ITEM_TYPES.map((t) => ({ id: t as string, name: t }))];

function layerLabel(layer: PackLayer): string {
  switch (layer) {
    case PackLayer.Client:
      return 'Client';
    case PackLayer.Practice:
      return 'Practice';
    default:
      return 'Regulatory';
  }
}

/** Keeps long JSON payloads readable in a grid cell. */
function summarise(json: string | null): string {
  if (!json) {
    return '';
  }
  return json.length <= 90 ? json : `${json.slice(0, 90)}...`;
}

const conflictText = (id: string, header: string, read: (row: PackConflictRow) => string | null) => ({
  id,
  header,
  cell: read,
});

const changeColumns: DataTableColumn<PackConflictRow>[] = [
  conflictText('itemKey', 'Item', (row) => row.itemKey),
  conflictText('itemType', 'Type', (row) => row.itemType),
  conflictText('changeKind', 'Change', (row) => row.changeKindText),
  conflictText('summary', 'What happened', (row) => row.summary),
];

const previewColumns: DataTableColumn<PackPreviewRow>[] = [
  { id: 'itemType', header: 'Type', cell: (row) => row.itemType },
  { id: 'itemKey', header: 'Item', cell: (row) => row.itemKey },
  { id: 'outcome', header: 'Outcome on publish', cell: (row) => row.outcome },
];

const resultColumns: DataTableColumn<GoldenFixtureResult>[] = [
  { id: 'name', header: 'Fixture', cell: (row) => row.name },
  { id: 'frameworkCode', header: 'Framework', cell: (row) => row.frameworkCode },
  { id: 'entityTypeCode', header: 'Entity', cell: (row) => row.entityTypeCode },
  { id: 'outputProfileCode', header: 'Output', cell: (row) => row.outputProfileCode },
  {
    id: 'passed',
    header: 'Result',
    cell: (row) => (
      <span className={`badge ${row.passed ? 'badge-ok' : 'badge-stale'}`}>{row.passed ? 'Passed' : 'Failed'}</span>
    ),
  },
  { id: 'message', header: 'Detail', cell: (row) => row.message },
];

const layerColumns: DataTableColumn<PackLayerRow>[] = [
  { id: 'itemType', header: 'Type', cell: (row) => row.itemType },
  { id: 'itemKey', header: 'Item', cell: (row) => row.itemKey },
  {
    id: 'base',
    header: 'Regulatory pack',
    cellClassName: 'onelineclamp',
    cell: (row) => summarise(row.baseJson),
  },
  {
    id: 'practice',
    header: 'Practice layer',
    cellClassName: 'onelineclamp',
    cell: (row) => summarise(row.practiceJson),
  },
  {
    id: 'client',
    header: 'Client override',
    cellClassName: 'onelineclamp',
    cell: (row) => summarise(row.clientJson),
  },
  {
    id: 'effective',
    header: 'Effective',
    cell: (row) => (
      <>
        <span className="badge">{layerLabel(row.effectiveLayer)}</span>
        {row.isSuppressed && <span className="badge badge-stale">Suppressed</span>}
      </>
    ),
  },
];

export function RegulatoryPackUpgrade() {
  const mounted = useMounted();

  const [selectedTab, setSelectedTab] = useState(0);
  const [packs, setPacks] = useState<PracticePackRow[]>([]);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const selectedPackIdRef = useRef<string | null>(null);
  /** ap5-a6 (item 114): distinguishes "still loading" from "this firm has no practice packs". */
  const [isPacksLoaded, setIsPacksLoaded] = useState(false);
  const [isCreatingPack, setIsCreatingPack] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [newPackFramework, setNewPackFramework] = useState<string | null>('FRS102-1A');

  const [layers, setLayers] = useState<PackLayerRow[]>([]);
  const [itemTypeFilter, setItemTypeFilter] = useState(ALL);
  const [layerSearch, setLayerSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  const [upgrade, setUpgrade] = useState<PackUpgradeDetail | null>(null);
  const [preview, setPreview] = useState<PackPreviewRow[]>([]);
  const [fixtures, setFixtures] = useState<GoldenFixtureRow[]>([]);
  const [regression, setRegression] = useState<GoldenRunResult | null>(null);

  /** The resolution chosen in the grid but not yet recorded, keyed by conflict id (insertion order is kept). */
  const [pendingResolutions, setPendingResolutions] = useState<Map<string, PackConflictResolution>>(new Map());

  const selectPack = (id: string | null) => {
    selectedPackIdRef.current = id;
    setSelectedPackId(id);
  };

  const selectedPack = packs.find((p) => p.id === selectedPackId) ?? null;
  const unresolvedCount = upgrade?.upgrade.unresolvedConflicts ?? 0;
  const hasPendingResolutions = pendingResolutions.size > 0;

  const loadLayers = useCallback(
    (packId: string | null, itemType: string, search: string) => {
      if (!packId) {
        setLayers([]);
        return;
      }
      practicePackService
        .getLayers(packId, { itemType: itemType || null, search: search || null })
        .then((result) => {
          if (mounted.current) setLayers(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [mounted],
  );

  const loadPacks = useCallback(() => {
    practicePackService
      .getPacks()
      .then((result) => {
        if (!mounted.current) return;
        setPacks(result);
        setIsPacksLoaded(true);
        // ap5-a6 (item 112): keep whatever the user has already chosen; otherwise leave the choice to them.
        const current = selectedPackIdRef.current;
        if (current && !result.some((p) => p.id === current)) {
          selectedPackIdRef.current = null;
          setSelectedPackId(null);
          setLayers([]);
        }
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  }, [mounted]);

  useEffect(() => {
    loadPacks();
    practicePackService
      .getFixtures()
      .then((result) => {
        if (mounted.current) setFixtures(result);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPackChange = (packId: string | null) => {
    setUpgrade(null);
    setPreview([]);
    setRegression(null);
    setPendingResolutions(new Map());
    loadLayers(packId, itemTypeFilter, layerSearch);
  };

  /**
   * ap5-a6 (item 114): a practice pack is created per firm and nothing in the app created one, so the picker was
   * permanently empty. The createPack endpoint already existed; this is the missing way in.
   */
  const createPack = () => {
    const frameworkCode = (newPackFramework ?? '').trim();
    if (!frameworkCode) {
      notifyError('Choose the framework the practice pack customises.');
      return;
    }

    practicePackService
      .createPack({ frameworkCode, name: newPackName?.trim() || null })
      .then((pack) => {
        if (!mounted.current) return;
        notifySuccess(`Practice pack "${pack.name}" created.`);
        setIsCreatingPack(false);
        setNewPackName('');
        setPacks((current) => [pack, ...current]);
        selectPack(pack.id);
        onPackChange(pack.id);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  /** Compares the published practice pack against the newest regulatory pack for its framework. */
  const compare = () => {
    if (!selectedPackId) {
      notifyError('Select a practice pack first.');
      return;
    }
    practicePackService
      .compareUpgrade({ practicePackId: selectedPackId, toBasePackVersionId: null })
      .then((detail) => {
        if (!mounted.current) return;
        setUpgrade(detail);
        setRegression(detail.latestRegressionRun);
        setPreview([]);
        setPendingResolutions(new Map());
        setSelectedTab(1);
        notifySuccess(
          `${detail.upgrade.changeCount} change(s) found, ${detail.upgrade.conflictCount} of them conflicting.`,
        );
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const chosenResolution = (conflict: PackConflictRow): PackConflictResolution | null =>
    pendingResolutions.get(conflict.id) ?? conflict.resolution;

  const onResolutionChange = (conflict: PackConflictRow, resolution: PackConflictResolution | null) =>
    setPendingResolutions((current) => {
      const next = new Map(current);
      if (resolution === null || resolution === undefined) {
        next.delete(conflict.id);
      } else {
        next.set(conflict.id, resolution);
      }
      return next;
    });

  /** Records every resolution chosen in the grid as one authorised action. */
  const applyResolutions = () => {
    if (!upgrade || pendingResolutions.size === 0) return;

    const byId = new Map(upgrade.conflicts.map((c) => [c.id, c]));
    const resolutions = [...pendingResolutions.entries()].map(([id, resolution]) => ({
      packConflictId: id,
      resolution,
      resolutionNote: null,
      mergedPayloadJson: null,
      expectedETag: byId.get(id)?.eTag ?? null,
    }));

    practicePackService
      .resolveConflicts({ packUpgradeId: upgrade.upgrade.id, resolutions })
      .then((detail) => {
        if (!mounted.current) return;
        setUpgrade(detail);
        setRegression(detail.latestRegressionRun);
        setPendingResolutions(new Map());
        notifySuccess(`${resolutions.length} change(s) resolved.`);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const loadPreview = () => {
    if (!upgrade) {
      notifyError('Compare against a regulatory pack first.');
      return;
    }
    practicePackService
      .getPreview(upgrade.upgrade.id)
      .then((rows) => {
        if (mounted.current) setPreview(rows);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const refreshUpgrade = (upgradeId: string) => {
    practicePackService
      .getUpgrade(upgradeId)
      .then((detail) => {
        if (!mounted.current) return;
        setUpgrade(detail);
        setRegression(detail.latestRegressionRun);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  /** Proves the previewed pack still resolves everything the golden accounts depend on. */
  const runRegression = () => {
    if (!upgrade) return;
    const upgradeId = upgrade.upgrade.id;
    practicePackService
      .runRegression(upgradeId)
      .then((run) => {
        if (!mounted.current) return;
        setRegression(run);
        if (run.passed) {
          notifySuccess(`Golden-account regression passed for ${run.passedCount} fixture(s).`);
        } else {
          notifyError(`Golden-account regression failed for ${run.failedCount} fixture(s).`);
        }
        refreshUpgrade(upgradeId);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const publish = () => {
    if (!upgrade) return;
    practicePackService
      .publishPack({ packUpgradeId: upgrade.upgrade.id, expectedETag: upgrade.upgrade.eTag })
      .then((response) => {
        if (!mounted.current) return;
        notifySuccess(response.message);
        setUpgrade(null);
        setPreview([]);
        setRegression(null);
        loadPacks();
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const conflictColumns: DataTableColumn<PackConflictRow>[] = [
    conflictText('itemKey', 'Item', (row) => row.itemKey),
    conflictText('itemType', 'Type', (row) => row.itemType),
    conflictText('changeKind', 'Change', (row) => row.changeKindText),
    conflictText('summary', 'What happened', (row) => row.summary),
    {
      id: 'resolution',
      header: 'Resolution',
      cell: (row) => (
        <>
          <Select<ResolutionOption>
            className="w-250"
            panelClass="myPanelClass"
            items={resolutionOptions}
            bindLabel="name"
            bindValue="id"
            placeholder="Choose a resolution"
            clearable={false}
            searchable={false}
            value={chosenResolution(row)}
            onChange={(value) => onResolutionChange(row, value as PackConflictResolution | null)}
          />
          {row.resolvedAtUtc && <span className="badge">Recorded</span>}
        </>
      ),
    },
  ];

  const layersTab = (
    <>
      <div className="content-header mb-0 pt-1">
        <div className="content-header-left mb-1">
          <FormField className="search-field" focused={searchFocused}>
            <Icon name="search" />
            <input
              className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
              placeholder="Search by item identifier"
              autoComplete="off"
              value={layerSearch}
              onChange={(event) => setLayerSearch(event.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') loadLayers(selectedPackId, itemTypeFilter, layerSearch);
              }}
            />
          </FormField>
        </div>
        <div className="content-header-right mb-1">
          <Select<{ id: string; name: string }>
            className="w-200"
            openOnFocus
            panelClass="myPanelClass"
            items={itemTypeOptions}
            bindLabel="name"
            bindValue="id"
            clearable={false}
            searchable={false}
            value={itemTypeFilter}
            onChange={(value) => {
              const itemType = value as string;
              setItemTypeFilter(itemType);
              loadLayers(selectedPackId, itemType, layerSearch);
            }}
          />
        </div>
      </div>

      <p className="mb-1">
        Each item resolves through three layers: the regulatory base pack, the firm's practice layer, then a controlled
        client override. The winning layer is shown on the right.
      </p>

      <div className="mat-table-wrapper">
        <DataTable
          stickyHeader
          className="tr-leaf-sticky-header"
          columns={layerColumns}
          rows={layers}
          rowKey={(row) => `${row.itemType}/${row.itemKey}`}
        />
        {layers.length === 0 && <p className="mt-1">No items match these filters.</p>}
      </div>
    </>
  );

  const conflictsTab = (
    <>
      {!upgrade && (
        <div className="pt-1">
          <p>Compare the practice pack with the latest regulatory pack to see what changed.</p>
        </div>
      )}

      {upgrade && (
        <div>
          <div className="content-header mb-0 pt-1">
            <div className="content-header-left mb-1">
              <span className="fw-bold">
                {upgrade.upgrade.changeCount} change(s) &middot; {upgrade.upgrade.conflictCount} conflict(s) &middot;{' '}
                {unresolvedCount} awaiting a decision
              </span>
            </div>
            <div className="content-header-right mb-1">
              <Button
                className="action-button primary-button"
                disabled={!hasPendingResolutions}
                onClick={applyResolutions}
              >
                <Icon name="done_all" />
                <span>Record resolutions</span>
              </Button>
            </div>
          </div>

          <p className="mb-1">
            A conflict is a regulatory change to an item this practice has customised. Publishing is refused until each
            one carries an explicit decision, so an upgrade cannot silently discard a customisation.
          </p>

          <div className="mat-table-wrapper mat-table-wrapper-pagination">
            <DataTable
              stickyHeader
              className="tr-leaf-sticky-header"
              columns={conflictColumns}
              rows={upgrade.conflicts}
              rowKey={(row) => row.id}
            />
            {upgrade.conflicts.length === 0 && (
              <p className="mt-1">
                No conflicts: every regulatory change applies cleanly and each practice customisation is preserved.
              </p>
            )}
          </div>

          <h3 className="mt-2">Non-conflicting changes</h3>
          <p className="mb-1">These apply automatically on publish because the practice has not customised them.</p>
          <div className="mat-table-wrapper mat-table-wrapper-full">
            <DataTable
              stickyHeader
              className="tr-leaf-sticky-header"
              columns={changeColumns}
              rows={upgrade.nonConflictingChanges}
              rowKey={(row) => row.id}
            />
            {upgrade.nonConflictingChanges.length === 0 && <p className="mt-1">No non-conflicting changes.</p>}
          </div>
        </div>
      )}
    </>
  );

  const previewTab = (
    <>
      {!upgrade && (
        <div className="pt-1">
          <p>Compare the practice pack with the latest regulatory pack first.</p>
        </div>
      )}

      {upgrade && (
        <div>
          <div className="content-header mb-0 pt-1">
            <div className="content-header-left mb-1">
              <Button className="action-button primary-button" onClick={loadPreview}>
                <Icon name="visibility" />
                <span>Preview resolved pack</span>
              </Button>
              <Button className="action-button primary-button ml-1" onClick={runRegression}>
                <Icon name="fact_check" />
                <span>Run golden-account regression</span>
              </Button>
            </div>
            {regression && (
              <div className="content-header-right mb-1">
                <span className={`fw-bold ${regression.passed ? 'text-success' : 'text-danger'}`}>
                  {regression.passed ? 'Regression passed' : 'Regression failed'} &middot; {regression.passedCount}{' '}
                  passed, {regression.failedCount} failed
                </span>
              </div>
            )}
          </div>

          <h3>Golden accounts</h3>
          <p className="mb-1">
            Each fixture states the reporting codes, disclosure fields and rules its statements depend on. The run
            proves the upgraded pack still resolves every one of them, by identifier rather than by comparing rendered
            text.
          </p>

          {regression && (
            <div className="mat-table-wrapper mat-table-wrapper-full">
              <DataTable
                stickyHeader
                className="tr-leaf-sticky-header"
                columns={resultColumns}
                rows={regression.results}
                rowKey={(row) => row.fixtureCode}
              />
            </div>
          )}

          {!regression && (
            <p className="mt-1">
              {fixtures.length} fixture(s) are available. The regression has not been run for this upgrade yet.
            </p>
          )}

          <h3 className="mt-2">Resolved pack preview</h3>
          <div className="mat-table-wrapper mat-table-wrapper-full">
            <DataTable
              stickyHeader
              className="tr-leaf-sticky-header"
              columns={previewColumns}
              rows={preview}
              rowKey={(row) => `${row.itemType}/${row.itemKey}`}
            />
            {preview.length === 0 && <p className="mt-1">Select "Preview resolved pack" to see every item.</p>}
          </div>
        </div>
      )}
    </>
  );

  const publishTab = (
    <>
      {!upgrade && (
        <div className="pt-1">
          <p>Compare the practice pack with the latest regulatory pack first.</p>
        </div>
      )}

      {upgrade && (
        <div className="pt-1">
          <h3>Publish a new practice pack version</h3>
          <p className="mb-1">
            Publishing issues the next version number and supersedes the current one. Periods and final documents that
            already pinned an earlier version keep resolving exactly what they pinned, so historical accounts reproduce
            unchanged.
          </p>

          <div className="form-field">
            <p>
              <span className="fw-bold">From:</span> {upgrade.fromBaseName}
            </p>
            <p>
              <span className="fw-bold">To:</span> {upgrade.upgrade.toBaseName}
            </p>
            <p>
              <span className="fw-bold">Practice layer:</span> {upgrade.practiceName} (version{' '}
              {upgrade.practiceVersionNo})
            </p>
          </div>

          {!upgrade.canPublish && (
            <div className="form-field">
              <p className="text-danger">
                <Icon name="error_outline" className="align-middle" />
                {upgrade.blockedReason}
              </p>
            </div>
          )}

          <Button className="action-button primary-button" disabled={!upgrade.canPublish} onClick={publish}>
            <Icon name="publish" />
            <span>Publish practice pack</span>
          </Button>
        </div>
      )}
    </>
  );

  return (
    <SideMenuPage menu="settings" contentClassName="regulatory-pack-upgrade">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select<PracticePackRow>
              className="w-350"
              openOnFocus
              panelClass="myPanelClass"
              items={packs}
              bindLabel="name"
              bindValue="id"
              placeholder="Select a practice pack"
              clearable={false}
              value={selectedPackId}
              onChange={(value) => {
                const packId = value as string | null;
                selectPack(packId);
                onPackChange(packId);
              }}
            />

            <Button className="action-button primary-button ml-1" disabled={!selectedPackId} onClick={compare}>
              <Icon name="compare_arrows" />
              <span>Compare with latest pack</span>
            </Button>

            {/* ap5-a6 (item 114): a practice pack is created per firm; nothing in the app created one. */}
            {!isCreatingPack && (
              <Button className="action-button secondary-button ml-1" onClick={() => setIsCreatingPack(true)}>
                <Icon name="add" />
                <span>New practice pack</span>
              </Button>
            )}

            {isCreatingPack && (
              <>
                <Select<string>
                  className="w-150 ml-1"
                  items={packFrameworks}
                  placeholder="Framework"
                  clearable={false}
                  value={newPackFramework}
                  onChange={(value) => setNewPackFramework(value as string | null)}
                />
                <InputField
                  fieldClassName="ml-1"
                  placeholder="Pack name (optional)"
                  charMode="alphanumeric"
                  value={newPackName}
                  onChange={(event) => setNewPackName(event.target.value)}
                  autoComplete="off"
                />
                <Button className="action-button primary-button ml-1" onClick={createPack}>
                  Create
                </Button>
                <Button className="action-button secondary-button ml-1" onClick={() => setIsCreatingPack(false)}>
                  Cancel
                </Button>
              </>
            )}
          </div>

          {isPacksLoaded && packs.length === 0 && (
            <div className="info-message primary-message mb-1">
              This firm has no practice packs yet. Create one to customise a regulatory pack and to compare it against
              the latest published version.
            </div>
          )}
          {isPacksLoaded && packs.length > 0 && !selectedPackId && (
            <div className="info-message primary-message mb-1">
              Select a practice pack to see its layers and to compare it with the latest regulatory pack.
            </div>
          )}

          {selectedPack && (
            <div className="content-header-right mb-1">
              <span className="fw-bold">
                Version {selectedPack.versionNo} &middot; {selectedPack.statusText} &middot; {selectedPack.basePackName}
              </span>
            </div>
          )}
        </div>

        <TabGroup
          alignTabs="start"
          selectedIndex={selectedTab}
          onSelectedIndexChange={setSelectedTab}
          tabs={[
            {
              label: 'Layers',
              infoText: 'The regulatory pack layers that make up this upgrade, in the order they apply.',
              children: layersTab,
            },
            {
              label: 'Conflicts and resolutions',
              infoText: 'Overlaps between layers and how each one was resolved.',
              children: conflictsTab,
            },
            {
              label: 'Preview and regression',
              infoText: 'A preview of the upgraded content and a regression check against the current pack.',
              children: previewTab,
            },
            { label: 'Publish', infoText: 'Review and publish the upgraded regulatory pack.', children: publishTab },
          ]}
        />
      </div>
    </SideMenuPage>
  );
}
